import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BarChart } from "@mantine/charts";
import {
  Anchor,
  Badge,
  Center,
  Grid,
  Group,
  NumberInput,
  Paper,
  ScrollArea,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconClipboardCheck,
  IconClipboardPlus,
  IconFlask,
} from "@tabler/icons-react";

import { OverviewCard } from "@pages/AdminDashboard/components/OverviewCard";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
  TruncatedCell,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { labStatusColors } from "@constants/colors";
import { DEFAULT_PAGINATION, ROLES } from "@constants/index";

import { useGetAnalysisActivity } from "@hooks/queries/dashboard/useGetAnalysisActivity";
import { useGetLabsTrend } from "@hooks/queries/dashboard/useGetLabsTrend";
import { useGetTotalLabStatuses } from "@hooks/queries/dashboard/useGetTotalLabStatuses";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { mq } from "@styles/Utilities.styled";

import { DateRangeSelect } from "../components/DateRangeSelect";


export const Analyses = () => {
  const { t, i18n } = useTranslation();

  const [dateRange, setDateRange] = useState<string | null>("last-7-days");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);
  const [analysisActivityDateRange, setAnalysisActivityDateRange] = useState<
    string | null
  >("last-7-days");
  const [customAnalysisActivityDateRange, setCustomAnalysisActivityDateRange] =
    useState<[Date | null, Date | null]>([null, null]);
  const isMedium = useMediaQuery(`(${mq.medium})`);
  const [currentPage, setCurrentPage] = useState(1);
  const { columnCount, tableRef } = useTableColumnCount();

  const {
    data: labsTrend,
    isLoading: isLabsTrendLoading,
    isError: isLabsTrendError,
    isSuccess: isLabsTrendSuccess,
  } = useGetLabsTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: totalLabStatuses,
    isLoading: isTotalLabsStatusesLoading,
    isError: isTotalLabsStatusesError,
    isSuccess: isTotalLabsStatusesSuccess,
  } = useGetTotalLabStatuses({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: analysisActivityData,
    isLoading: isAnalysisActivityLoading,
    isSuccess: isAnalysisActivitySuccess,
    isError: isAnalysisActivityError,
  } = useGetAnalysisActivity({
    pagination: {
      page: currentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    dateRange: analysisActivityDateRange as string,
    ...(analysisActivityDateRange === "custom" &&
      getCustomDateRange(customAnalysisActivityDateRange)),
  });

  const analysisRows = useMemo(() => {
    return analysisActivityData?.newData?.map(item => (
      <Table.Tr key={item.id}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {formatDateToString(item.date as Date)}
            </Text>
            <Text c="dimmed" fz={13}>
              {item.date?.toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Group gap={8} align="start">
            <Stack gap={5}>
              <Anchor
                component={Link}
                to={`/patients/${item.patientId}/consultation/general/${item.consultationId}`}
                fz={14}
                c="accent"
                ff={"var(--font-secondary)"}>
                {item.cId}
              </Anchor>
              <Text c="dimmed" fz={13}>
                {formatDateToString(item.consultationDate as Date)},{" "}
                {new Date(item.consultationDate as Date).toLocaleTimeString(
                  i18n.language,
                  { timeStyle: "short" }
                )}
              </Text>
            </Stack>
          </Group>
        </Table.Td>
        <Table.Td>
          <TruncatedCell value={item.type ?? ""} />
        </Table.Td>
        <Table.Td>{item.physician}</Table.Td>
        <Table.Td c={item?.supportStaff ? "black" : "dimmed"}>
          {item?.supportStaff ? item.supportStaff : "N/A"}
        </Table.Td>
        <Table.Td>
          <Badge
            miw={"max-content"}
            fz={12}
            c={labStatusColors.get(item.status as string)?.c}
            bg={labStatusColors.get(item.status as string)?.bg}>
            {item.status}
          </Badge>
        </Table.Td>
      </Table.Tr>
    ));
  }, [analysisActivityData, i18n.language]);

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={ROLES.ADMIN}>
        <Group mt={"1rem"} justify="start">
          <Text fz={15}>{t("displaying-stats-for")}</Text>
          <DateRangeSelect
            dateRange={dateRange}
            setDateRange={setDateRange}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
          />
        </Group>
        <SimpleGrid cols={{ base: 2, md: 3, lg1: 4 }} mt={"2rem"}>
          <OverviewCard
            title={t("total-labs")}
            amount={totalLabStatuses?.total || 0}
            isLoading={isTotalLabsStatusesLoading}
            icon={
              <Group
                bg="accent.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconFlask
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-accent-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("labs-ordered")}
            amount={totalLabStatuses?.ordered || 0}
            isLoading={isTotalLabsStatusesLoading}
            icon={
              <Group
                bg="blue.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconClipboardPlus
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-blue-6)"
                />
              </Group>
            }
          />
          <OverviewCard
            title={t("labs-completed")}
            amount={totalLabStatuses?.completed || 0}
            isLoading={isTotalLabsStatusesLoading}
            icon={
              <Group
                bg="green.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconClipboardCheck
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-green-6)"
                />
              </Group>
            }
          />
        </SimpleGrid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("labs-ordered-completed")}
                </Title>
              </Group>

              {renderLoadingErrorData({
                isLoading: isLabsTrendLoading,
                isError: isLabsTrendError,
                isSuccess: isLabsTrendSuccess,
                dataLength: labsTrend?.length || 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  // <Skeleton h={300} w={"100%"} mt={"1rem"} />
                  <Group h={300} mt={"1rem"} align="end" justify="space-evenly">
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"80%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"20%"} w={"8%"} />
                    <Skeleton h={"90%"} w={"8%"} />
                    <Skeleton h={"70%"} w={"8%"} />
                    <Skeleton h={"50%"} w={"8%"} />
                    <Skeleton h={"30%"} w={"8%"} />
                    <Skeleton h={"60%"} w={"8%"} />
                    {isMedium && <Skeleton h={"50%"} w={"8%"} />}
                  </Group>
                ),
                dataComponent: (
                  <BarChart
                    mt={"1rem"}
                    h={300}
                    data={labsTrend || []}
                    // xAxisLabel="Months"
                    // yAxisLabel="Consultations"
                    dataKey="date"
                    withLegend
                    tooltipAnimationDuration={150}
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    series={[
                      { name: "ordered", color: "indigo.5" },
                      { name: "completed", color: "lime.5" },
                    ]}
                    tickLine="y"
                    tt={"capitalize"}
                  />
                ),
              })}
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" mb="8rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("lab-activity")}
                </Title>
                <Group gap={"xs"}>
                  {/* <Button leftSection={<IconDownload size={16} />} disabled>
                    {t("export-csv")}
                  </Button> */}
                  <DateRangeSelect
                    dateRange={analysisActivityDateRange}
                    setDateRange={newRange => {
                      setAnalysisActivityDateRange(newRange);
                      setCurrentPage(1); // Reset to first page when date range changes
                    }}
                    customDateRange={customAnalysisActivityDateRange}
                    setCustomDateRange={newRange => {
                      setCustomAnalysisActivityDateRange(newRange);
                    }}
                  />
                </Group>
              </Group>

              <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }} h={"100%"}>
                <Table
                  ref={tableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1280}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"}>
                      <Table.Th>{t("date-created")}</Table.Th>
                      <Table.Th>{t("consultation")}</Table.Th>
                      <Table.Th>{t("lab-type")}</Table.Th>
                      <Table.Th>{t("doctor")}</Table.Th>
                      <Table.Th>{t("support-staff")}</Table.Th>
                      <Table.Th>{t("status")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isAnalysisActivityLoading,
                      isError: isAnalysisActivityError,
                      isSuccess: isAnalysisActivitySuccess,
                      dataLength: analysisActivityData?.newData?.length || 0,
                      emptyDataComponent: (
                        <TableEmptyRow
                          colSpan={columnCount}
                          message={t("empty-data")}
                        />
                      ),
                      errorComponent: (
                        <TableErrorRow
                          colSpan={columnCount}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={columnCount}
                          skeletonProps={{
                            widths: ["md", "md", "lg", "lg", "lg", "md"],
                          }}
                        />
                      ),
                      dataComponent: analysisRows,
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <TablePagination
                paginationData={analysisActivityData?.pagination || {}}
                paginatedRows={analysisActivityData?.newData?.length || 0}
                onPageChange={setCurrentPage}
                isLoading={isAnalysisActivityLoading}
              />
            </Paper>
          </Grid.Col>
        </Grid>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
